import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend_url } from "../App";
import "./CSS/ProductApprovals.css";

const FIELD_LABELS = {
  name: "Product Name", unique_id: "Product ID", description: "Description",
  category: "Category", subcategory: "Subcategory", old_price: "MRP / Price",
  new_price: "Discount %", quantity: "Quantity", scale: "Unit", material: "Material",
  weight: "Weight", size: "Width / Size", color: "Colour", quality: "Quality",
  disclaimer: "Disclaimer", note: "Note", washcare: "Wash Care", bestseller: "Bestseller",
  status: "Stock Status", images: "Images", colors: "Colour Variants",
};
// Order fields appear in on a card
const FIELD_ORDER = Object.keys(FIELD_LABELS);
// Shown on "new product" cards
const NEW_PRODUCT_FIELDS = ["unique_id", "category", "subcategory", "old_price", "new_price", "quantity", "scale", "status", "material", "color", "images"];

const TABS = [
  { value: "pending",  label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("adminToken")}`,
});

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60)    return "just now";
  if (s < 3600)  return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return new Date(d).toLocaleString();
};

const sameValue = (a, b) => JSON.stringify(a ?? "") === JSON.stringify(b ?? "") || String(a ?? "") === String(b ?? "");

// Renders one field value in a readable way
const Value = ({ field, value }) => {
  if (field === "images") {
    const list = (Array.isArray(value) ? value : []).filter(Boolean);
    if (!list.length) return <span className="pa-empty">No images</span>;
    return (
      <div className="pa-thumbs">
        {list.map((src) => (
          <a key={src} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" /></a>
        ))}
      </div>
    );
  }
  if (field === "colors") {
    const list = Array.isArray(value) ? value : [];
    if (!list.length) return <span className="pa-empty">None</span>;
    return (
      <div className="pa-swatches">
        {list.map((c, i) => (
          <span key={i} className="pa-swatch"><i style={{ background: c.hex }} />{c.name}</span>
        ))}
      </div>
    );
  }
  if (value === "" || value === null || value === undefined) return <span className="pa-empty">—</span>;
  if (field === "old_price") return <>₹{Number(value).toLocaleString("en-IN")}</>;
  if (field === "new_price") return <>{value}%</>;
  return <span className="pa-text" title={String(value)}>{String(value)}</span>;
};

const ProductApprovals = () => {
  const navigate = useNavigate();
  const [tab, setTab]           = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [rejectFor, setRejectFor] = useState(null); // array of ids awaiting a reason
  const [reason, setReason]     = useState("");
  const [pendingCount, setPendingCount] = useState(0);

  const load = async (status = tab) => {
    setLoading(true);
    setSelected([]);
    try {
      const res  = await fetch(`${backend_url}/api/product-requests?status=${status}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setRequests(data.requests);
        if (status === "pending") setPendingCount(data.requests.length);
      } else {
        toast.error(data.message || "Could not load requests");
      }
    } catch {
      toast.error("Could not load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((r) =>
      [r.productName, r.unique_id, r.requestedBy?.name].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [requests, search]);

  const allSelected = shown.length > 0 && shown.every((r) => selected.includes(r._id));
  const toggleAll = () => setSelected(allSelected ? [] : shown.map((r) => r._id));
  const toggleOne = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const review = async (action, ids, rejectReason = "") => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const res  = await fetch(`${backend_url}/api/product-requests/${action}`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ ids, reason: rejectReason }),
      });
      const data = await res.json();
      const verb = action === "approve" ? "approved" : "rejected";
      if (data.done) toast.success(`${data.done} request${data.done > 1 ? "s" : ""} ${verb}`);
      if (data.failed?.length) toast.error(`${data.failed.length} failed: ${data.failed.map((f) => f.message).join(", ")}`);
      if (!data.done && !data.failed?.length) toast.error(data.message || "Something went wrong");
      window.dispatchEvent(new Event("approvals-changed")); // refresh sidebar badge
      await load(tab);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const openReject = (ids) => { setReason(""); setRejectFor(ids); };
  const confirmReject = async () => {
    const ids = rejectFor;
    setRejectFor(null);
    await review("reject", ids, reason.trim());
  };

  return (
    <div className="pa-page">
      {/* Header */}
      <div className="pa-header">
        <div>
          <h1 className="pa-title">Product Approvals</h1>
          <p className="pa-sub">Changes made by staff wait here until you approve or reject them.</p>
        </div>
        {pendingCount > 0 && <span className="pa-count">{pendingCount} waiting</span>}
      </div>

      {/* Tabs */}
      <div className="pa-tabs">
        {TABS.map((t) => (
          <button key={t.value} className={`pa-tab ${tab === t.value ? "active" : ""}`}
            onClick={() => { setTab(t.value); setSearch(""); }}>
            {t.label}
            {t.value === "pending" && pendingCount > 0 && <span className="pa-tab-badge">{pendingCount}</span>}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="pa-toolbar">
        {tab === "pending" && shown.length > 0 && (
          <label className="pa-select-all">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            Select all
          </label>
        )}
        <input className="pa-search" placeholder="Search product, ID or user…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {tab === "pending" && selected.length > 0 && (
          <div className="pa-bulk">
            <span className="pa-bulk-count">{selected.length} selected</span>
            <button className="pa-btn approve" disabled={busy} onClick={() => review("approve", selected)}>
              ✓ Approve selected
            </button>
            <button className="pa-btn reject" disabled={busy} onClick={() => openReject(selected)}>
              ✕ Reject selected
            </button>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="pa-empty-state">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="pa-empty-state">
          <div className="pa-empty-icon">{tab === "pending" ? "🎉" : "📭"}</div>
          {tab === "pending" ? "All caught up — nothing waiting for approval." : `No ${tab} requests.`}
        </div>
      ) : (
        <div className="pa-list">
          {shown.map((r) => {
            const isNew   = r.type === "create";
            const product = r.product;
            const thumb   = (product?.images || r.changes?.images || [])[0];
            const fields  = isNew
              ? NEW_PRODUCT_FIELDS.filter((f) => r.changes?.[f] !== undefined)
              : FIELD_ORDER.filter((f) => f in (r.changes || {}));
            const isChecked = selected.includes(r._id);

            return (
              <div key={r._id} className={`pa-card ${isChecked ? "checked" : ""}`}>
                <div className="pa-card-head">
                  {tab === "pending" && (
                    <input type="checkbox" className="pa-check" checked={isChecked} onChange={() => toggleOne(r._id)} />
                  )}
                  {thumb ? <img className="pa-card-img" src={thumb} alt="" /> : <div className="pa-card-img pa-noimg">—</div>}
                  <div className="pa-card-info">
                    <div className="pa-card-title">
                      {r.productName || "Untitled product"}
                      <span className={`pa-type ${isNew ? "new" : "edit"}`}>{isNew ? "New product" : "Edit"}</span>
                    </div>
                    <div className="pa-card-meta">
                      {r.unique_id && <span className="pa-chip">{r.unique_id}</span>}
                      Requested by <strong>{r.requestedBy?.name || "user"}</strong>
                      {r.requestedBy?.role && <span className="pa-role">{r.requestedBy.role}</span>}
                      · {timeAgo(r.createdAt)}
                    </div>
                    {tab !== "pending" && (
                      <div className={`pa-review ${r.status}`}>
                        {r.status === "approved" ? "✓ Approved" : "✕ Rejected"} by {r.reviewedBy?.name || "admin"}
                        {r.reviewedAt && ` · ${new Date(r.reviewedAt).toLocaleString()}`}
                        {r.rejectReason && <> — <em>“{r.rejectReason}”</em></>}
                      </div>
                    )}
                  </div>
                  {tab === "pending" && (
                    <div className="pa-card-actions">
                      <button className="pa-btn approve" disabled={busy} onClick={() => review("approve", [r._id])}>✓ Approve</button>
                      <button className="pa-btn reject" disabled={busy} onClick={() => openReject([r._id])}>✕ Reject</button>
                      {!isNew && product && (
                        <button className="pa-btn ghost" onClick={() => navigate(`/editproduct/${product._id}`)}>Open product</button>
                      )}
                    </div>
                  )}
                </div>

                {/* What changes */}
                <table className="pa-diff">
                  <thead>
                    <tr>
                      <th>Field</th>
                      {!isNew && <th>Current</th>}
                      <th>{isNew ? "Value" : "Requested"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f) => {
                      const current = product?.[f];
                      const movedOn = !isNew && tab === "pending" && product && !sameValue(current, r.previous?.[f]);
                      return (
                        <tr key={f}>
                          <td className="pa-field">
                            {FIELD_LABELS[f] || f}
                            {movedOn && <span className="pa-warn" title="Someone changed this field after the request was made">changed since</span>}
                          </td>
                          {!isNew && (
                            <td className="pa-old"><Value field={f} value={tab === "pending" ? current : r.previous?.[f]} /></td>
                          )}
                          <td className="pa-new"><Value field={f} value={r.changes?.[f]} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject reason */}
      {rejectFor && (
        <div className="pa-modal-overlay" onClick={() => setRejectFor(null)}>
          <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reject {rejectFor.length > 1 ? `${rejectFor.length} requests` : "request"}?</h3>
            <p>The product stays as it is. You can add a note for the staff member (optional).</p>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Price is wrong, please check with the shop" autoFocus />
            <div className="pa-modal-actions">
              <button className="pa-btn ghost" onClick={() => setRejectFor(null)}>Cancel</button>
              <button className="pa-btn reject" onClick={confirmReject}>✕ Reject</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ProductApprovals;
